import type { AuthUser, MemberRole } from '@eriko/shared-types';
import type { InviteMemberInput, UpdateMemberRoleInput } from '@eriko/validation';
import { apiClient } from '../lib/api';
import { ENDPOINTS } from './endpoints';

export interface MemberItem {
  id: string;
  role: MemberRole;
  joinedAt: string | null;
  user: AuthUser;
}

export interface InvitationItem {
  id: string;
  email: string;
  role: MemberRole;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
}

export interface InvitationResponse {
  invitation: {
    id: string;
    email: string;
    role: MemberRole;
    token: string;
    expiresAt: string;
  };
}

// Matches GET /api/org/settings, which returns a flat object (the axios
// interceptor unwraps `data`). `settings` is the full OrganizationSettings row.
export interface OrgSettingsResponse {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  jurisdiction: string;
  jurisdictionSupported: boolean;
  settings: {
    industry: string | null;
    teamSize: string | null;
    defaultCurrency: string | null;
    aiPersonalization: unknown | null;
    adjustmentAccountId: string | null;
  } | null;
}

export async function getOrgSettings(): Promise<OrgSettingsResponse> {
  const res = await apiClient.get<OrgSettingsResponse>(ENDPOINTS.org.settings);
  return res.data;
}

export async function getMembers(): Promise<MemberItem[]> {
  const res = await apiClient.get<MemberItem[]>(ENDPOINTS.org.members);
  return res.data;
}

export async function getInvitations(): Promise<InvitationItem[]> {
  const res = await apiClient.get<InvitationItem[]>(ENDPOINTS.org.invitations);
  return res.data;
}

export async function inviteMember(input: InviteMemberInput): Promise<InvitationResponse> {
  const res = await apiClient.post<InvitationResponse>(ENDPOINTS.org.invite, input);
  return res.data;
}

export async function updateMemberRole(id: string, input: UpdateMemberRoleInput): Promise<{ message: string }> {
  const res = await apiClient.put<{ message: string }>(ENDPOINTS.org.memberRole(id), input);
  return res.data;
}

export async function removeMember(id: string): Promise<{ message: string }> {
  const res = await apiClient.delete<{ message: string }>(ENDPOINTS.org.member(id));
  return res.data;
}
